import { ref, computed, watch, unref, nextTick } from 'vue';
import { escapeSql } from '@rdrive/helper-sql';
import { tokens } from '@rdrive/theme';
import { biColumnSql } from '@/data/filters';

// Filter registration ids, unchanged from the old components/StatusFilterFab.vue. Each keys one
// context's registry AND a DuckDB view global to the connection (`filter_view_<id>`), so every id
// belongs to ONE context (never register one id on both — SKILL.md, issue-analysis's filter
// contexts).
const STATUS_ISSUES_ID = 'statusFilter';
const STATUS_HISTORY_ID = 'statusHistoryFilter';
const OVERDUE_ISSUES_ID = 'overdueFilter';
const OVERDUE_HISTORY_ID = 'overdueHistoryFilter';

/** The "overdue only" checkbox's key in the FAB's `extraToggles` / `extraToggleValues`. */
export const OVERDUE_TOGGLE = 'overdueOnly';

// A status template without a colour: the neutral grey token (#999 — the old literal '#999999',
// the same colour).
const STATUS_COLOR_FALLBACK = tokens.chart.othersColor;

/**
 * issue-analysis's global status + "overdue only" filter engine behind the package FAB
 * (`@rdrive/plugin-status-filter`'s `StatusFilterFab`, rendered by App/component.vue in the shell's `fab`
 * slot) — IA_ABSORPTION_PLAN Phase 4 step 13.2. The logic and SQL of the old store-coupled
 * `components/StatusFilterFab.vue`, store-free (project-overview's `useStatusTypeFilters` is the
 * model): the app passes its state as writable computeds and the contexts as computeds.
 *
 * It writes BOTH filter contexts, each under its own ids: a status selection → `statusFilter`
 * (issues) + `statusHistoryFilter` (status history); "overdue only" → `overdueFilter` +
 * `overdueHistoryFilter`. All statuses selected → both status filters removed; none → both
 * registered EMPTY; otherwise the two status queries. Drafts start deselected (`isDraft === 0`
 * selected), and "select/deselect all" acts on the statuses only, as before.
 *
 * Re-registration: every issue-analysis page resets its context(s) on mount, which wipes the
 * registry — the FAB's filters included. The old component re-applied everything 100 ms after a
 * route change; this composable re-applies when a context's registry is WIPED (resetFilters /
 * initializeFilterContext replace `ctx.filters`, and tick) while one of its filters should be
 * registered — whatever wiped it, without a timer. Two guards: (1) only a wipe counts (a new
 * `ctx.filters` object), never an ordinary apply's tick, so a filter that cannot be built (no
 * tick of its own) cannot start an apply → tick → apply loop; (2) the check waits until no apply
 * of this composable is in flight, so a wipe in the middle of one (a page resetting both
 * contexts) does not start a second, interleaved apply of the same filters.
 *
 * @param {object} deps
 * @param {object|import('vue').Ref} deps.conn  the dashboard's gated DuckDB connection
 * @param {boolean|import('vue').Ref} deps.dataLoaded
 * @param {boolean|import('vue').Ref} [deps.isInitialized]  DuckDB ready flag (gated as before)
 * @param {{ issues: object, statusHistory: object }} deps.contexts  each a ref/computed or a
 *   plain filter context; duck type { filters, filtersLastUpdated, detected, updateFilter,
 *   updateFilterWithQuery, removeFilter }
 * @param {object|import('vue').Ref} deps.statusTemplatesObj  the store's status-template lookup
 *   (`getStatusTemplatesObj`: id → status_templates row of the statuses issues use)
 * @param {import('vue').WritableComputedRef<Object>} deps.statusFilters  title → Boolean
 * @param {import('vue').WritableComputedRef<boolean>} deps.overdueOnly
 */
export function useIssueStatusFilters({
  conn,
  dataLoaded,
  isInitialized = true,
  contexts,
  statusTemplatesObj,
  statusFilters,
  overdueOnly
}) {
  const issuesCtx = () => unref(contexts.issues);
  const historyCtx = () => unref(contexts.statusHistory);

  // Local copy, replaced wholesale on every change (the old component's "better reactivity with
  // object replacement"; toggleAll relies on it being updated before nextTick()/applyFilters).
  const localFilters = ref({});

  // Sync local filters with the source of truth
  watch(() => statusFilters.value, (newFilters) => {
    if (newFilters && Object.keys(newFilters).length > 0) {
      localFilters.value = { ...newFilters };
    }
  }, { immediate: true, deep: true });

  // The statuses issues use, with colours, from the store's lookup — deduplicated by title (the
  // first after sorting by sort, then colour descending)
  const statusTemplates = computed(() => {
    const statusTemplatesById = unref(statusTemplatesObj);
    if (statusTemplatesById && Object.keys(statusTemplatesById).length > 0) {
      const uniqueStatuses = new Map();
      Object.values(statusTemplatesById)
        .filter(status => status && status.Title)
        .sort((a, b) => {
          const sortDiff = (a.Sort || 0) - (b.Sort || 0);
          if (sortDiff !== 0) return sortDiff;
          return (b.Color || '').localeCompare(a.Color || '');
        })
        .forEach(status => {
          if (!uniqueStatuses.has(status.Title)) {
            uniqueStatuses.set(status.Title, {
              title: status.Title,
              color: status.Color || STATUS_COLOR_FALLBACK,
              sort: status.Sort || 0,
              isDraft: status.IsDraft || 0
            });
          }
        });
      return Array.from(uniqueStatuses.values())
        .sort((a, b) => {
          const sortDiff = a.sort - b.sort;
          if (sortDiff !== 0) return sortDiff;
          return (b.color || '').localeCompare(a.color || '');
        });
    }
    return [];
  });

  // Check if all filters are selected ("overdue only" does not count, as before)
  const allFiltersSelected = computed(() => {
    const filterValues = Object.values(localFilters.value);
    return filterValues.length > 0 && filterValues.every(v => v === true);
  });

  const hasActiveStatusFilter = computed(() => {
    const filterValues = Object.values(localFilters.value);
    return filterValues.length > 0 && filterValues.some(v => v === false);
  });

  // The FAB's outline: at least one status deselected, or "overdue only" on
  const hasActiveFilter = computed(() => hasActiveStatusFilter.value || overdueOnly.value);

  // The FAB's extra checkbox state, keyed like its `extraToggles`
  const extraToggleValues = computed(() => ({ [OVERDUE_TOGGLE]: overdueOnly.value }));

  // Toggle individual filter
  const toggleStatus = (statusTitle) => {
    const newFilters = { ...localFilters.value };
    newFilters[statusTitle] = !newFilters[statusTitle];
    localFilters.value = newFilters;
    statusFilters.value = newFilters;
  };

  // Toggle an extra filter by its key (the FAB's `toggleExtra`): "overdue only"
  const toggleExtra = (key) => {
    if (key === OVERDUE_TOGGLE) {
      overdueOnly.value = !overdueOnly.value;
    }
  };

  const applyStatusAndOverdue = async () => {
    const selectedStatuses = Object.keys(statusFilters.value).filter(status => statusFilters.value[status]);
    const totalStatuses = Object.keys(statusFilters.value).length;

    if (!unref(isInitialized) || !unref(dataLoaded)) {
      return;
    }

    if (!unref(conn)) return;

    try {
      // Handle overdue filter. `BI-DueBy` is an optional column of `issues` (the project's field
      // tags): read through the issues context's detection — a typed NULL when it is absent, so
      // nothing is overdue (the old query named the column regardless: a Binder Error, and the
      // filter was never applied).
      if (overdueOnly.value) {
        const dueBy = biColumnSql(issuesCtx(), 'BI-DueBy');
        const overdueQuery = `
        SELECT DISTINCT i.Id
        FROM issues i
        INNER JOIN status_templates st ON i.IssueStatusId = st.Id
        WHERE ${dueBy} IS NOT NULL
          AND ${dueBy} != ''
          AND TRY_CAST(${dueBy} AS TIMESTAMP) < NOW()
          AND st.IsTerminal = false
      `;
        const overdueStatusHistoryQuery = `
        SELECT DISTINCT sht.subItemId, sht.validFrom, sht.statusId
        FROM status_history_temporal sht
        INNER JOIN issues i ON sht.subItemId = i.Id
        INNER JOIN status_templates st ON i.IssueStatusId = st.Id
        WHERE ${dueBy} IS NOT NULL
          AND ${dueBy} != ''
          AND TRY_CAST(${dueBy} AS TIMESTAMP) < NOW()
          AND st.IsTerminal = false
      `;
        await issuesCtx().updateFilterWithQuery(OVERDUE_ISSUES_ID, overdueQuery);
        await historyCtx().updateFilterWithQuery(OVERDUE_HISTORY_ID, overdueStatusHistoryQuery);
      } else {
        await issuesCtx().removeFilter(OVERDUE_ISSUES_ID);
        await historyCtx().removeFilter(OVERDUE_HISTORY_ID);
      }

      // If all statuses are selected, remove the status filter
      if (selectedStatuses.length === totalStatuses) {
        await issuesCtx().removeFilter(STATUS_ISSUES_ID);
        await historyCtx().removeFilter(STATUS_HISTORY_ID);
        return;
      }

      // If no statuses selected, show nothing
      if (selectedStatuses.length === 0) {
        await issuesCtx().updateFilter(STATUS_ISSUES_ID, []);
        await historyCtx().updateFilter(STATUS_HISTORY_ID, []);
        return;
      }

      // Build SQL query for partial selection
      const statusList = selectedStatuses.map(s => `'${escapeSql(s)}'`).join(', ');

      const issuesQuery = `
      SELECT DISTINCT i.Id
      FROM issues i
      INNER JOIN status_templates st ON i.IssueStatusId = st.Id
      WHERE st.Title IN (${statusList})
    `;

      const statusHistoryQuery = `
      SELECT DISTINCT sht.subItemId, sht.validFrom, sht.statusId
      FROM status_history_temporal sht
      INNER JOIN status_templates st ON sht.statusId = st.Id
      WHERE st.Title IN (${statusList})
    `;

      await issuesCtx().updateFilterWithQuery(STATUS_ISSUES_ID, issuesQuery);
      await historyCtx().updateFilterWithQuery(STATUS_HISTORY_ID, statusHistoryQuery);
    } catch (error) {
      console.error('Error applying status filters:', error);
    }
  };

  // Every apply is tracked while it runs, so the re-registration below can wait for it.
  const inFlight = new Set();
  const applyFilters = () => {
    const run = applyStatusAndOverdue();
    inFlight.add(run);
    run.finally(() => inFlight.delete(run));
    return run;
  };
  const applyIdle = async () => {
    while (inFlight.size > 0) {
      await Promise.all([...inFlight]);
    }
  };

  // Toggle all filters (select all or deselect all) — the statuses only
  const toggleAll = async () => {
    const newValue = !allFiltersSelected.value;
    const newFilters = {};
    statusTemplates.value.forEach(status => {
      newFilters[status.title] = newValue;
    });
    localFilters.value = { ...newFilters };
    statusFilters.value = newFilters;
    await nextTick();
    await applyFilters();
  };

  // Watch for changes in statusTemplates to initialize filters (drafts deselected)
  watch(statusTemplates, (newTemplates) => {
    if (newTemplates.length > 0) {
      if (Object.keys(statusFilters.value).length === 0) {
        const newFilters = {};
        newTemplates.forEach(status => {
          newFilters[status.title] = status.isDraft === 0;
        });
        statusFilters.value = newFilters;
        applyFilters();
      }
    }
  }, { immediate: true });

  // Watch for changes in filters and apply them in realtime
  watch(() => statusFilters.value, () => {
    applyFilters();
  }, { deep: true });

  // Watch for changes in overdueOnly and apply filter
  watch(() => overdueOnly.value, () => {
    applyFilters();
  });

  // Watch for data loaded to reapply existing filters
  watch(() => unref(dataLoaded), (isLoaded) => {
    if (isLoaded && Object.keys(statusFilters.value).length > 0) {
      applyFilters();
    }
  });

  // Re-register after a WIPE of a context's registry (see the header): `ctx.filters` is
  // markRaw and replaced — not cleared — by resetFilters/initializeFilterContext, so comparing
  // the object read LIVE on each tick tells a wipe from an ordinary apply. Read
  // `.filtersLastUpdated.value` directly (a re-registration guard, not a refresh — never through
  // useFilterContextRefresh) and never snapshot `filters`.
  const reregisterOnWipe = (ctx, wantedIds) => {
    let registry = null;
    watch(() => ctx().filtersLastUpdated.value, async () => {
      const current = ctx().filters;
      if (current === registry) return;
      registry = current;
      if (!unref(dataLoaded)) return;
      await applyIdle();
      const registered = ctx().filters;
      if (wantedIds().some(id => !(id in registered))) {
        applyFilters();
      }
    });
  };
  reregisterOnWipe(issuesCtx, () => [
    ...(hasActiveStatusFilter.value ? [STATUS_ISSUES_ID] : []),
    ...(overdueOnly.value ? [OVERDUE_ISSUES_ID] : [])
  ]);
  reregisterOnWipe(historyCtx, () => [
    ...(hasActiveStatusFilter.value ? [STATUS_HISTORY_ID] : []),
    ...(overdueOnly.value ? [OVERDUE_HISTORY_ID] : [])
  ]);

  return {
    statusTemplates,
    allFiltersSelected,
    hasActiveStatusFilter,
    hasActiveFilter,
    extraToggleValues,
    toggleStatus,
    toggleExtra,
    toggleAll
  };
}
